/**
 * Cart page gallery — uses the same modal / slick / zoom stack as the PDP.
 */
(function () {
  const MODAL_ID = 'cartImageGalleryModal';
  const productCache = new Map();
  function getModal() {
    return document.getElementById(MODAL_ID);
  }

  function getBlock() {
    return getModal()?.querySelector('[data-cart-gallery-block]');
  }

  function normalizeImageUrl(url) {
    if (!url) return '';
    if (url.startsWith('//')) return `https:${url}`;
    return url;
  }

  function stripSizeSuffix(url) {
    return url.replace(/_(?:\d+x\d*|compact|grande|large|medium|small|pico|icon|thumb)(?=\.|$)/gi, '');
  }

  function buildSlidesFromProduct(product, fallbackSrc) {
    const list = [];
    const seen = new Set();

    function pushSlide(src, alt, thumbSrc) {
      const full = normalizeImageUrl(stripSizeSuffix(src));
      if (!full || seen.has(full)) return;
      seen.add(full);
      list.push({
        src: full,
        thumb: normalizeImageUrl(thumbSrc || src),
        alt: alt || product.title || '',
      });
    }

    if (product.media && product.media.length) {
      product.media.forEach((media) => {
        if (media.media_type && media.media_type !== 'image') return;
        const src = media.src || media.preview_image?.src;
        if (src) pushSlide(src, media.alt, media.preview_image?.src || src);
      });
    }

    if (product.images && product.images.length) {
      product.images.forEach((src) => pushSlide(src, product.title, src));
    }

    if (!list.length && fallbackSrc) {
      pushSlide(fallbackSrc, product.title, fallbackSrc);
    }

    return list;
  }

  function findInitialIndex(slides, fallbackSrc) {
    if (!fallbackSrc || !slides.length) return 0;
    const target = normalizeImageUrl(stripSizeSuffix(fallbackSrc));
    const idx = slides.findIndex((s) => {
      const a = stripSizeSuffix(s.src);
      const b = stripSizeSuffix(s.thumb);
      return a === target || b === target || target.includes(a) || a.includes(target);
    });
    return idx >= 0 ? idx : 0;
  }

  async function fetchProduct(handle) {
    if (productCache.has(handle)) return productCache.get(handle);
    const root = window.Shopify?.routes?.root || '/';
    const response = await fetch(`${root}products/${handle}.js`, {
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) throw new Error('Product fetch failed');
    const product = await response.json();
    productCache.set(handle, product);
    return product;
  }

  function setLoading(block) {
    if (!block) return;
    block.innerHTML = '<div class="cart-image-gallery-block-loading">Afbeeldingen laden…</div>';
  }

  function resolveProductUrl(productUrl, handle) {
    const path = productUrl || `/products/${handle}`;
    if (path.startsWith('http')) return path;
    return `${window.location.origin}${path.startsWith('/') ? path : `/${path}`}`;
  }

  function populateModalHeader(trigger) {
    const modal = getModal();
    if (!modal || !trigger) return;

    const productUrl = resolveProductUrl(trigger.dataset.productUrl, trigger.dataset.productHandle);
    const productTitle = trigger.dataset.productTitle || '';
    const altName = trigger.dataset.productAltName || '';

    const titleEl = modal.querySelector('[data-cart-gallery-title]');
    const altEl = modal.querySelector('[data-cart-gallery-alt-name]');
    const linkEl = modal.querySelector('[data-cart-gallery-product-link]');

    if (titleEl) titleEl.textContent = productTitle;
    if (altEl) {
      if (altName) {
        altEl.textContent = `Ook bekend als ${altName}`;
        altEl.hidden = false;
      } else {
        altEl.textContent = '';
        altEl.hidden = true;
      }
    }
    if (linkEl) {
      linkEl.href = productUrl;
      linkEl.setAttribute('aria-label', `Bekijk ${productTitle || 'product'}`);
    }
  }

  function renderGallery(slides, startIndex, options) {
    const modal = getModal();
    const block = getBlock();
    const api = window.ProductImageModal;
    const reopen = options && options.reopen;

    if (!modal || !block || !api || !slides.length) return;

    api.destroy(modal);
    block.innerHTML = api.buildGalleryHtml(slides);

    if (reopen && api.isOpen(modal)) {
      api.afterOpen(modal, startIndex);
    } else {
      api.open(modal, startIndex);
    }
  }

  function openFromTrigger(trigger) {
    const modal = getModal();
    const api = window.ProductImageModal;

    if (!modal || !api || !trigger) return;

    const handle = trigger.dataset.productHandle;
    const productTitle = trigger.dataset.productTitle;
    const fallbackSrc = trigger.dataset.imageSrc;

    if (!handle) return;

    populateModalHeader(trigger);

    setLoading(getBlock());

    const slides = fallbackSrc
      ? [
          {
            src: normalizeImageUrl(fallbackSrc),
            thumb: normalizeImageUrl(fallbackSrc),
            alt: productTitle || '',
          },
        ]
      : [];

    renderGallery(slides, 0);

    fetchProduct(handle)
      .then((product) => {
        const loadedSlides = buildSlidesFromProduct(product, fallbackSrc);
        const startIndex = findInitialIndex(loadedSlides, fallbackSrc);
        renderGallery(loadedSlides, startIndex, { reopen: true });
      })
      .catch(() => {
        if (slides.length) renderGallery(slides, 0, { reopen: true });
      });
  }

  function bindTriggers() {
    document.addEventListener('click', (event) => {
      const trigger = event.target.closest('.cart-item-gallery-trigger');
      if (!trigger) return;
      event.preventDefault();
      event.stopPropagation();
      openFromTrigger(trigger);
    });

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      const trigger = event.target.closest('.cart-item-gallery-trigger');
      if (!trigger) return;
      event.preventDefault();
      openFromTrigger(trigger);
    });
  }

  function bindFooterActions() {
    const modal = getModal();
    const api = window.ProductImageModal;
    if (!modal || !api) return;

    const backBtn = modal.querySelector('[data-cart-gallery-back]');
    if (backBtn && backBtn.dataset.bound !== 'true') {
      backBtn.addEventListener('click', () => api.close(modal));
      backBtn.dataset.bound = 'true';
    }
  }

  function init() {
    const modal = getModal();
    const api = window.ProductImageModal;

    if (!modal || !api) return;

    api.moveToBody(modal);
    api.bindClose(modal);
    bindFooterActions();
    bindTriggers();
  }

  function waitForApi() {
    if (window.ProductImageModal && window.ProductImageZoom) {
      init();
      return;
    }
    setTimeout(waitForApi, 50);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', waitForApi);
  } else {
    waitForApi();
  }
})();
